/**
 * HelpPanel — Slide-out panel listing available walkthroughs.
 */
import { motion, AnimatePresence } from "framer-motion";
import {
  Rocket,
  LayoutDashboard,
  Users,
  CalendarDays,
  Mail,
  Globe,
  BarChart3,
  Crown,
  Kanban,
  BookOpen,
  Play,
  Check,
  RotateCcw,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useWalkthrough, walkthroughRegistry } from "@/hooks/walkthroughs";
import type { WalkthroughConfig } from "@/hooks/walkthroughs/types";

const iconMap: Record<string, React.ElementType> = {
  Rocket,
  LayoutDashboard,
  Users,
  CalendarDays,
  Mail,
  Globe,
  BarChart3,
  Crown,
  Kanban,
  BookOpen,
};

function StatusBadge({ status }: { status?: string }) {
  if (status === "completed")
    return (
      <Badge variant="outline" className="text-[10px] text-primary border-primary/20 bg-primary/5">
        Completed
      </Badge>
    );
  if (status === "in_progress")
    return (
      <Badge variant="outline" className="text-[10px] text-accent-foreground border-accent bg-accent/50">
        In Progress
      </Badge>
    );
  return null;
}

export default function HelpPanel() {
  const { helpPanelOpen, toggleHelpPanel, startWalkthrough, getProgress, resetProgress } = useWalkthrough();

  const overviewGuides = walkthroughRegistry.filter((w) => w.category === "getting-started");
  const pageGuides = walkthroughRegistry.filter((w) => w.category === "pages");

  const handleStart = (config: WalkthroughConfig) => {
    startWalkthrough(config);
  };

  const handleRestart = (e: React.MouseEvent, config: WalkthroughConfig) => {
    e.stopPropagation();
    resetProgress(config.id);
    startWalkthrough(config);
  };

  return (
    <AnimatePresence>
      {helpPanelOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 z-[9990]"
            onClick={toggleHelpPanel}
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-[380px] max-w-[90vw] bg-card border-l border-border shadow-2xl z-[9991] flex flex-col"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-border flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground">Learn MemberOS</h2>
                  <p className="text-[11px] text-muted-foreground">Guided walkthroughs & help</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={toggleHelpPanel} type="button" className="h-7 w-7">
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {/* Overview */}
              <Section title="Getting Started">
                {overviewGuides.map((guide) => (
                  <GuideCard
                    key={guide.id}
                    guide={guide}
                    progress={getProgress(guide.id)}
                    onStart={() => handleStart(guide)}
                    onRestart={(e) => handleRestart(e, guide)}
                  />
                ))}
              </Section>

              {/* Page Guides */}
              <Section title="Page Guides">
                {pageGuides.map((guide) => (
                  <GuideCard
                    key={guide.id}
                    guide={guide}
                    progress={getProgress(guide.id)}
                    onStart={() => handleStart(guide)}
                    onRestart={(e) => handleRestart(e, guide)}
                  />
                ))}
              </Section>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2.5">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

interface GuideCardProps {
  guide: WalkthroughConfig;
  progress?: { status: string };
  onStart: () => void;
  onRestart: (e: React.MouseEvent) => void;
}

function GuideCard({ guide, progress, onStart, onRestart }: GuideCardProps) {
  const Icon = guide.icon ? iconMap[guide.icon] || BookOpen : BookOpen;
  const isCompleted = progress?.status === "completed";

  return (
    <button
      type="button"
      onClick={onStart}
      className="w-full text-left rounded-xl border border-border bg-background p-3.5 hover:bg-accent/50 hover:border-primary/20 transition-all group"
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
            isCompleted ? "bg-green-100 text-green-600" : "bg-primary/10 text-primary"
          }`}
        >
          {isCompleted ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-medium text-foreground truncate">{guide.title}</span>
            <StatusBadge status={progress?.status} />
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2">{guide.description}</p>
          <div className="flex items-center gap-2 mt-1.5">
            {guide.duration && <span className="text-[10px] text-muted-foreground/70">{guide.duration}</span>}
            <span className="text-[10px] text-muted-foreground/70">• {guide.steps.length} steps</span>
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {isCompleted && (
            <button type="button" onClick={onRestart} className="p-1 rounded hover:bg-muted" title="Restart">
              <RotateCcw className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          )}
          <Play className="w-3.5 h-3.5 text-primary" />
        </div>
      </div>
    </button>
  );
}
